import { MAX_SIGNAL_BYTES, PROTOCOL_VERSION } from './constants';

export interface EncryptedEnvelope {
  v: number;
  iv: string;
  ciphertext: string;
  createdAt: number;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replaceAll('-', '+').replaceAll('_', '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/u, '');
}

export async function deriveRoomKey(secret: string, roomId: string): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey('raw', fromBase64Url(secret), 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: encoder.encode(roomId), info: encoder.encode('pairferry/signaling/v1') },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptJson(key: CryptoKey, value: unknown): Promise<EncryptedEnvelope> {
  const plaintext = encoder.encode(JSON.stringify(value));
  if (plaintext.byteLength > MAX_SIGNAL_BYTES) throw new Error('Signaling message is too large.');
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { v: PROTOCOL_VERSION, iv: toBase64Url(iv), ciphertext: toBase64Url(new Uint8Array(ciphertext)), createdAt: Date.now() };
}

export async function decryptJson<T>(key: CryptoKey, envelope: EncryptedEnvelope): Promise<T> {
  if (envelope.v !== PROTOCOL_VERSION) throw new Error('The other browser uses an incompatible protocol version.');
  if (envelope.ciphertext.length > MAX_SIGNAL_BYTES * 2) throw new Error('Encrypted signaling message is too large.');
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: fromBase64Url(envelope.iv) },
    key,
    fromBase64Url(envelope.ciphertext),
  );
  return JSON.parse(decoder.decode(plaintext)) as T;
}

export async function sha256(data: BufferSource): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', data);
  return toBase64Url(new Uint8Array(digest));
}
